import { csiAPI } from "src/boot/axios"
import { CSIurl } from "src/util/enums/urls"
import { concatUrl, getCustomConfig, getNewAbortHandler } from "src/util/http/httpHelper"
import { Attachment, AttachmentPatch } from "src/util/types/dataModels/attachment"
import { ObjectList } from "src/util/types/dataModels/genericObject"
import { HttpConfig } from "src/util/types/dataModels/http"
import {
  PERMISSION_ACCESS_READ,
  PERMISSION_ACCESS_UPDATE,
  PERMISSION_FILES,
} from "src/util/types/dataModels/user"

/**
 * DO NOT USE THIS UNLESS YOU KNOW WHAT YOU ARE DOING.
 *
 * Get the list of all existing Attachments
 *
 * @param config custom configurations and filtering params
 */
export async function getAttachmentsList(config: HttpConfig = {}): Promise<ObjectList<Attachment>> {
  const entityURL = CSIurl.ATTACHMENTS_LIST
  const url = concatUrl(entityURL, { ...config?.mainParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_FILES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

/**
 * DO NOT USE THIS UNLESS YOU KNOW WHAT YOU ARE DOING.
 *
 * Get an individual Attachment
 *
 * @param config custom configurations and filtering params
 * @param attachmentId
 */
export async function getAttachment(
  config: HttpConfig = {},
  attachmentId: string
): Promise<Attachment> {
  const url = CSIurl.ATTACHMENTS_ID.replace("{id}", attachmentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_FILES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

/**
 * Partially update an individual Attachment
 *
 * @param config custom configurations and filtering params
 * @param attachmentId the id of the Attachment that should be partially updated
 * @param attachmentData
 */
export async function patchAttachment(
  config: HttpConfig = {},
  attachmentId: string,
  attachmentData: AttachmentPatch
): Promise<Attachment> {
  const url = CSIurl.ATTACHMENTS_ID.replace("{id}", attachmentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_FILES, PERMISSION_ACCESS_UPDATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, attachmentData, { signal, customConfig })

  return data
}

/**
 * Get the content of an individual Attachment
 *
 * @param config custom configurations and filtering params
 * @param attachmentId
 */
export async function getAttachmentContent(
  config: HttpConfig = {},
  attachmentId: string
): Promise<Blob> {
  const url = CSIurl.ATTACHMENTS_ID_CONTENT.replace("{id}", attachmentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const headers = {
    "Content-Type": "multipart/form-data",
  }
  const customConfig = getCustomConfig(abortHandler, PERMISSION_FILES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { headers, signal, responseType: "blob", customConfig })

  return data
}

/**
 * Patch Attachments in bulk
 *
 * @param config custom configurations and filtering params
 * @param attachments
 */
export async function patchAttachmentsBatch(
  config: HttpConfig = {},
  attachments: AttachmentPatch[]
): Promise<ObjectList<Attachment>> {
  const url = CSIurl.ATTACHMENTS_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_FILES, PERMISSION_ACCESS_UPDATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, attachments, { signal, customConfig })

  return data
}
